/**
 * WhatsApp templates "synchronization" may return a plain JSON array or a Spring Data–style page.
 * When the response is paged, this helper loads every page using ?page=&size= (same as other Meta endpoints).
 */
type TemplatesSynchronizationPage = {
  content: unknown[];
  last?: boolean;
  number?: number;
  size?: number;
  totalPages?: number;
  totalElements?: number;
  numberOfElements?: number;
  first?: boolean;
  page?: {
    last?: boolean;
    number?: number;
    size?: number;
    totalPages?: number;
    totalElements?: number;
  };
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

function asTemplatesPage(data: unknown): TemplatesSynchronizationPage | null {
  if (!isRecord(data)) return null;
  const content = data.content;
  if (!Array.isArray(content)) return null;
  return data as TemplatesSynchronizationPage;
}

function pageNumber(page: TemplatesSynchronizationPage): number {
  return page.number ?? page.page?.number ?? 0;
}

function pageSize(page: TemplatesSynchronizationPage): number {
  const fromMeta = page.size ?? page.page?.size;
  if (typeof fromMeta === 'number' && fromMeta > 0) {
    return fromMeta;
  }
  const len = page.content.length;
  return len > 0 ? len : 1;
}

function totalElements(page: TemplatesSynchronizationPage): number | undefined {
  const te = page.totalElements ?? page.page?.totalElements;
  return typeof te === 'number' && Number.isFinite(te) ? te : undefined;
}

function totalPages(page: TemplatesSynchronizationPage): number | undefined {
  const tp = page.totalPages ?? page.page?.totalPages;
  if (typeof tp === 'number' && tp > 0) return tp;
  const te = totalElements(page);
  const size = pageSize(page);
  if (te !== undefined && size > 0) {
    return Math.max(1, Math.ceil(te / size));
  }
  return undefined;
}

/**
 * After loading `page`, returns true if another page should be requested.
 * Important: some backends omit `last` / `totalPages`; stopping only when both are missing
 * caused the client to keep only the first page (~100 rows) and miss the rest.
 */
function shouldFetchNextPage(page: TemplatesSynchronizationPage, mergedLength: number): boolean {
  const number = pageNumber(page);
  const size = pageSize(page);
  const contentLen = page.content.length;
  const last = page.last ?? page.page?.last;

  if (last === true) {
    return false;
  }

  const tp = totalPages(page);
  if (tp !== undefined && number + 1 >= tp) {
    return false;
  }

  const te = totalElements(page);
  if (te !== undefined && mergedLength >= te) {
    return false;
  }

  // Short page with no total metadata → natural end of list (`last === true` handled above)
  if (tp === undefined && te === undefined && contentLen < size) {
    return false;
  }

  // Full page but no reliable "last" metadata → assume more pages may exist
  return true;
}

const MAX_SYNCHRONIZATION_PAGES = 500;

export async function fetchAllWhatsappTemplatesSynchronization(
  synchronizationBasePath: string,
  get: (path: string) => Promise<unknown>
): Promise<unknown[]> {
  const first = await get(synchronizationBasePath);

  if (Array.isArray(first)) {
    return first;
  }

  const firstPage = asTemplatesPage(first);
  if (!firstPage) {
    return [];
  }

  let merged = [...firstPage.content];
  let current = firstPage;

  for (let i = 0; i < MAX_SYNCHRONIZATION_PAGES; i++) {
    if (!shouldFetchNextPage(current, merged.length)) {
      break;
    }

    const number = pageNumber(current);
    const size = pageSize(current);
    const sep = synchronizationBasePath.includes('?') ? '&' : '?';
    const nextUrl = `${synchronizationBasePath}${sep}page=${number + 1}&size=${size}`;
    const nextRaw = await get(nextUrl);

    if (Array.isArray(nextRaw)) {
      merged = merged.concat(nextRaw);
      break;
    }

    const nextPage = asTemplatesPage(nextRaw);
    if (!nextPage) {
      break;
    }

    if (nextPage.content.length === 0) {
      break;
    }

    const beforeLen = merged.length;
    merged = merged.concat(nextPage.content);
    if (merged.length === beforeLen) {
      break;
    }

    current = nextPage;
  }

  return merged;
}
